import React, {useState, useEffect, useRef} from 'react';
import {Spin, message, Button, Slider} from 'antd';
import {useSelector} from 'react-redux';
import {useNewIdleRouteReportMutation} from '../../../slices/rootApiSlices/reportApiSlice';
import {
    PlayCircleOutlined,
    CloseCircleOutlined,
    PauseCircleOutlined,
    StopOutlined,
} from '@ant-design/icons';
import {
    GoogleMap,
    OverlayView,
    Polyline,
    useJsApiLoader,
} from '@react-google-maps/api';
import HistorySideBar from './HistorySideBar';
import damlaPng from '../../../assets/blackmarker.png';

const CustomMarker = ({index, playIndex, text, color, damlaPng}) => {
    if (index === playIndex) {
        return (
            <img
                src={damlaPng}
                alt="Damla Marker"
                style={{
                    width: '30px',
                    height: '30px',
                    transform: 'translate(-50%, -50%)',
                }}
            />
        );
    }

    return (
        <div
            style={{
                backgroundColor: color,
                borderRadius: '50%',
                width: '22px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                transform: 'translate(-50%, -50%)',
            }}
        >
            {text}
        </div>
    );
};

const HistoryMap = ({vehicleId, timeRange, onExit}) => {

    const {isLoaded} = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyC-SvUIH5jgmfFvb5DXjuawroi1CG134ZA',
    });

    const [data, setData] = useState([]);
    const [mapCenter, setMapCenter] = useState({lat: 39.9334, lng: 32.8597});
    const [playIndex, setPlayIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [skipIndex, setSkipIndex] = useState(0);
    const [endDate, setEndDate] = useState(new Date());
    const [startDate, setStartDate] = useState(new Date());
    const playIntervalRef = useRef(null);
    const {user} = useSelector((state) => state.auth);
    const [idleHandle, {isLoading}] = useNewIdleRouteReportMutation();

    useEffect(() => {
        let newEndDate = new Date();
        let newStartDate = new Date(newEndDate);

        if (timeRange === 'history-1h') {
            newStartDate.setHours(newEndDate.getHours() - 1);
        } else if (timeRange === 'history-2h') {
            newStartDate.setHours(newEndDate.getHours() - 2);
        } else if (timeRange === 'history-3h') {
            newStartDate.setHours(newEndDate.getHours() - 3);
        } else if (timeRange === 'history-4h') {
            newStartDate.setHours(newEndDate.getHours() - 4);
        } else if (timeRange === 'history-5h') {
            newStartDate.setHours(newEndDate.getHours() - 5);
        } else if (timeRange === 'today') {
            newStartDate.setHours(0, 0, 0, 0);
        } else if (timeRange === 'yesterday') {
            newStartDate.setDate(newEndDate.getDate() - 1);
            newStartDate.setHours(0, 0, 0, 0);
            newEndDate.setDate(newEndDate.getDate() - 1);
            newEndDate.setHours(23, 59, 59, 999);
        } else if (
            typeof timeRange === 'object' &&
            timeRange?.startDate &&
            timeRange.endDate
        ) {
            newStartDate = new Date(timeRange.startDate);
            newEndDate = new Date(timeRange.endDate);
        }
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [timeRange]);

    const adjustToUTCPlus3 = (date) => {
        const adjustedDate = new Date(date);
        adjustedDate.setHours(date.getHours());
        return adjustedDate;
    };

    const formatDate = (date) => {
        return adjustToUTCPlus3(date).toISOString();
    };

    const handleQuery = async () => {
        try {
            const res = await idleHandle({
                id: vehicleId,
                token: user?.token,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                resolution: '8s',
                map: 'true',
            }).unwrap();
            setData(res?.data);
            if (res?.data.length > 0) {
                setMapCenter({
                    lat: res.data[0].gps.lat,
                    lng: res.data[0].gps.lon,
                });
            }
        } catch (e) {
            message.error(
                e?.data?.messageTR ||
                'Bir hata oluştu. Lütfen bir süre sonra tekrar deneyiniz.'
            );
            onExit();
        }
    };

    useEffect(() => {
        if (startDate && endDate) {
            handleQuery();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleId, startDate, endDate]);

    let lastValidCondition = null;

    const filledData = data.map((item, index) => {
        if (item.conditions && item.conditions.length > 0) {
            lastValidCondition = item.conditions;
            return {...item};
        } else {
            return {
                ...item,
                conditions: lastValidCondition || [],
            };
        }
    });

    const pathCoordinates = data.map((item) => ({
        lat: item.gps.lat,
        lng: item.gps.lon,
    }));

    useEffect(() => {
        if (isPlaying && playIndex < data.length - 1) {
            playIntervalRef.current = setInterval(() => {
                setPlayIndex((prevIndex) => {
                    const newIndex = prevIndex + 1;
                    setMapCenter({
                        lat: data[newIndex].gps.lat,
                        lng: data[newIndex].gps.lon,
                    });
                    return newIndex;
                });
            }, 1000 / playbackSpeed);
        } else {
            clearInterval(playIntervalRef.current);
        }
        return () => clearInterval(playIntervalRef.current);
    }, [isPlaying, playIndex, data, playbackSpeed]);

    const handlePlayPause = () => {
        setIsPlaying(!isPlaying);
    };

    const handleStop = () => {
        setIsPlaying(false);
        setPlayIndex(0);
        setSkipIndex(0);
        if (data.length > 0) {
            setMapCenter({lat: data[0].gps.lat, lng: data[0].gps.lon});
        }
    };

    const handlePlaybackSpeedChange = (value) => {
        setPlaybackSpeed(value);
    };

    const handleSkipChange = (value) => {
        setSkipIndex(value);
        setPlayIndex(value);
        if (data.length > 0) {
            setMapCenter({lat: data[value].gps.lat, lng: data[value].gps.lon});
        }
    };

    const handleItemSelect = (index) => {
        setPlayIndex(index);
        setMapCenter({lat: data[index].gps.lat, lng: data[index].gps.lon});
    };

    if (!isLoaded || isLoading) {
        return (
            <Spin
                size="medium"
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            />
        );
    }

    return (
        <div
            style={{
                height: 'calc(100vh - 4.6em)',
                width: '100%',
                position: 'relative',
            }}
        >
            <HistorySideBar
                data={data}
                currentIndex={playIndex}
                onItemSelect={handleItemSelect}
            />
            <div
                style={{
                    position: 'absolute',
                    top: 100,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: 10,
                    borderRadius: 5,
                    width: '20%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '5px',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        style={{flex: 1}}
                        icon={isPlaying ? <PauseCircleOutlined/> : <PlayCircleOutlined/>}
                        onClick={handlePlayPause}
                    />
                    <Button style={{flex: 1}} icon={<StopOutlined/>} onClick={handleStop}/>
                    <Button
                        style={{flex: 1}}
                        icon={<CloseCircleOutlined/>}
                        onClick={onExit}
                    />
                </div>
                <div style={{marginTop: '10px'}}>
                    <label>Oynatma Hızı</label>
                    <Slider
                        min={0.5}
                        max={4}
                        step={0.5}
                        value={playbackSpeed}
                        onChange={handlePlaybackSpeedChange}
                    />
                </div>
                <div style={{marginTop: '10px'}}>
                    <label>İleri/Geri Sarma</label>
                    <Slider
                        min={0}
                        max={data.length - 1}
                        step={1}
                        value={skipIndex}
                        onChange={handleSkipChange}
                    />
                </div>
            </div>
            <GoogleMap
                mapContainerStyle={{
                    height: 'calc(100vh - 4.6em)',
                    width: '100%',
                    boxSizing: 'border-box',
                    position: 'relative',
                }}
                center={mapCenter}
                zoom={19}
                gestureHandling={'greedy'}
                zoomControl={true}
                options={{disableDefaultUI: true, mapTypeId: 'satellite'}}
            >
                {filledData.map((item, index) => {
                    let color = 'blue';
                    if (item.conditions?.length > 0) {
                        const status = item.conditions[0].status;
                        if (status === 'yellow') color = '#fddb27'; // Sarı renk
                        else if (status === 'red') color = '#ff4d4d'; // Kırmızı renk
                    }
                    return (
                        <OverlayView
                            key={index}
                            position={{lat: item.gps.lat, lng: item.gps.lon}}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div onClick={() => handleItemSelect(index)}>
                                <CustomMarker
                                    index={index}
                                    playIndex={playIndex}
                                    text={index + 1}
                                    color={color}
                                    damlaPng={damlaPng}
                                />
                            </div>
                        </OverlayView>
                    );
                })}
                <Polyline
                    path={pathCoordinates}
                    options={{
                        strokeColor: '#3a83e3',
                        strokeOpacity: 0.8,
                        strokeWeight: 5,
                    }}
                />
            </GoogleMap>
        </div>
    );
};

export default HistoryMap;
